// src/components/NavBar.js
import React, { useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import DirectJump from "./DirectJump";
import { FaTimes, FaHome, FaBookmark, FaChartBar, FaHands, FaQuran, FaInfoCircle, FaTags } from "react-icons/fa";
import { LiaHandsSolid } from "react-icons/lia";

function NavBar({ isOpen, onClose }) {
  const navRef = useRef(null);

  const handleClickOutside = useCallback(
    (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        onClose();
      }
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  const handleBackButton = useCallback(() => {
    if (isOpen) onClose();
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      window.addEventListener("popstate", handleBackButton);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("popstate", handleBackButton);
    };
  }, [isOpen, handleClickOutside, handleKeyDown, handleBackButton]);

  const navLinks = [
    { to: "/", label: "Home", icon: <FaHome className="w-5 h-5 inline-block mr-2" /> },
    { to: "/bookmarks", label: "Bookmarks", icon: <FaBookmark className="w-5 h-5 inline-block mr-2" /> },
    { to: "/progress", label: "Reading Progress", icon: <FaChartBar className="w-5 h-5 inline-block mr-2" /> },
    { to: "/topics-test", label: "Topics Test", icon: <FaTags className="w-5 h-5 inline-block mr-2" /> },
    { to: "/duas", label: "All Duas", icon: <FaHands className="w-5 h-5 inline-block mr-2" /> },
    { to: "/duacategories", label: "Dua Categories", icon: <LiaHandsSolid className="w-5 h-5 inline-block mr-2" /> },
    { to: "/13linepage", label: "13 Line", icon: <FaQuran className="w-5 h-5 inline-block mr-2" /> },
    { to: "/about", label: "About", icon: <FaInfoCircle className="w-5 h-5 inline-block mr-2" /> },
  ];

  return (
    <nav
      ref={navRef}
      aria-label="Main Navigation"
      className={`fixed top-0 left-0 h-screen w-72 max-w-[85vw] bg-gray-900 text-white transform ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      } transition-transform duration-300 ease-in-out z-50`}
    >
      <div className="p-4 flex justify-between items-center bg-gray-800">
        <h2 className="text-xl font-bold">Quran</h2>
        <button
          onClick={onClose}
          aria-label="Close Navigation"
          className="text-white focus:outline-none hover:text-teal-400 transition-colors duration-100"
        >
          <FaTimes className="w-6 h-6" />
        </button>
      </div>

      <ul className="space-y-2 bg-gray-800 h-[calc(100%-4rem)] overflow-y-auto pb-8">
        {navLinks.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              onClick={onClose}
              className="flex items-center px-4 py-2 hover:bg-gray-700 hover:text-teal-400 transition-colors duration-200"
            >
              {link.icon}
              {link.label}
            </Link>
          </li>
        ))}

        <li className="px-3 py-3">
          <p className="text-xs uppercase tracking-wide text-gray-400 px-1 mb-2">Go To</p>
          {isOpen && <DirectJump variant="drawer" onNavigate={onClose} />}
        </li>
      </ul>
    </nav>
  );
}

NavBar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NavBar;
